import { Injectable } from '@angular/core';
import { Unsubscribe } from 'firebase/database';
import { FirebaseService } from '../firebase/firebase.service';

@Injectable({
  providedIn: 'root'
})
export class SignalingTransportService {
  public connectionState: string = 'Disconnected';

  private roomId: string;
  private userId: string;
  private messageUnsubscribe: Unsubscribe;
  private connectionUnsubscribe: Unsubscribe;

  constructor(
    private firebase: FirebaseService,
  ) {
    this.setupConnectionMonitor();
  }

  public connect(roomId: string, userId: string): void {
    this.roomId = roomId;
    this.userId = userId;
    this.firebase.database.refOnDisconnect(this.signalingPath(roomId, userId)).remove();
  }

  public async subscribe(roomId: string, userId: string, callback: (data: any) => void): Promise<void> {
    this.unsubscribe();
    const path = this.signalingPath(roomId, userId);
    await this.firebase.database.refRemove(path);
    this.messageUnsubscribe = this.firebase.database.refListenChildAdded(path, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        callback(data);
      }
      this.firebase.database.refRemove(`${path}/${snapshot.key}`);
    });
  }

  public unsubscribe(): void {
    if (this.messageUnsubscribe) {
      this.messageUnsubscribe();
      this.messageUnsubscribe = null;
    }
  }

  public async publish(roomId: string, recipientId: string, data: any): Promise<void> {
    const path = this.signalingPath(roomId, recipientId);
    this.firebase.database.refPush(path, JSON.parse(JSON.stringify(data)));
  }

  public disconnect(roomId: string): void {
    this.unsubscribe();
    if (roomId && this.userId) {
      this.firebase.database.refRemove(this.signalingPath(roomId, this.userId));
    }
    this.roomId = null;
    this.userId = null;
  }

  public async isReady(): Promise<void> {
    if (this.connectionState === 'Connected') {
      return;
    }

    return new Promise((resolve) => {
      let retry = 0;
      const interval = setInterval(() => {
        if (this.connectionState === 'Connected' || retry > 30) {
          clearInterval(interval);
          resolve();
        }
        retry++;
      }, 1000);
    });
  }

  private setupConnectionMonitor(): void {
    this.connectionUnsubscribe = this.firebase.database.refListenValue('.info/connected', (snap) => {
      this.connectionState = snap.val() === true ? 'Connected' : 'Disconnected';
    });
  }

  private signalingPath(roomId: string, userId: string): string {
    return `signaling/${roomId}/${userId}`;
  }
}
